import { useState, useRef, useEffect } from 'preact/hooks';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import {
  getPrevNextKeyUsingRef,
  getFirstVisibleKey,
  getKey,
  keyExtractor,
  findElementByKey
} from '../../utils/PRIVATE_collectionUtils';
import { useCollectionFocusRing } from '../PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../PRIVATE_useCurrentKey';
import { useId } from '../UNSAFE_useId';

import { ComponentProps, ContextType, RefObject } from 'preact';
import type { TabBarContext, TabBarLayout } from '../../UNSAFE_TabBarCommon';

type TabBarContextValue = ContextType<typeof TabBarContext>;

export type useTabBarOptions = {
  class?: string;
  display?: TabBarContextValue['display'];
  edge?: 'bottom' | 'top';
  onRemove?: TabBarContextValue['onRemove'];
  onSelect?: TabBarContextValue['onSelect'];
  ref?: RefObject<HTMLDivElement>;
  selection?: TabBarContextValue['selection'];
  size?: TabBarContextValue['size'];
  children?: ComponentProps<typeof TabBarLayout>['children'];
};

const ITEM_SELECTOR = '[role="tab"]';
const REMOVABLE_ICON_SELECTOR = '[data-oj-tabbar-item-remove-icon="true"]';
const REMOVABLE_ITEM_ATTRIBUTE = 'data-oj-removable';

/**
 * Implements TabBar behavior for focus and keyboad handling
 */
export function useTabBar<K extends string | number>(options: useTabBarOptions) {
  const {
    children,
    class: className,
    display,
    edge = 'top',
    selection,
    onSelect,
    onRemove,
    ref: rootRef = useRef<HTMLDivElement>(null),
    size
  } = options;

  const tabId = useId();
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection as K);

  const tabItemPrefix = tabId + '_';

  /**
   * A reference to the previous set of tab keys that this TabBar
   * contains before children were re-rendered. In the event the "current" tab
   * is removed and the "currentKey" reference is broken, we can redirect
   * the "currentKey" to another tab based on the index of the removed tab.
   */
  const prevTabKeys = useRef<(string | number | undefined)[]>();

  useEffect(() => {
    if (rootRef.current) {
      const tabKeys = Array.from(rootRef.current.querySelectorAll(ITEM_SELECTOR)).map((elem) =>
        getKey(elem as HTMLElement)
      );
      if (currentKey && !tabKeys.includes(currentKey as K)) {
        //If prevTabs does not exist set the first visible key as currentKey
        prevTabKeys.current
          ? setCurrentKey(findNextValidKey(currentKey, tabKeys, prevTabKeys.current) as K)
          : setCurrentKey(tabKeys[0] as K);
      }
      prevTabKeys.current = tabKeys;
    }
  }, [children, currentKey]);

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
    'ArrowRight',
    'ArrowLeft',
    'Home',
    'End'
  ]);

  const { currentKeyProps } = useCurrentKey(
    (element) =>
      onRemove
        ? extractOnlyItemKey(element, ITEM_SELECTOR, REMOVABLE_ICON_SELECTOR)
        : keyExtractor(element, ITEM_SELECTOR),
    false,
    undefined,
    undefined,
    getPrevNextKeyUsingRef(rootRef, currentKey, true, ITEM_SELECTOR),
    getPrevNextKeyUsingRef(rootRef, currentKey, false, ITEM_SELECTOR),
    currentKey,
    (detail) => setCurrentKey(detail.value as K)
  );

  const onKeyDown = (event: KeyboardEvent) => {
    if (rootRef.current && currentKey) {
      if (event.key === 'Home' || event.key === 'End') {
        const tabBarItemKey = Array.from(rootRef.current.querySelectorAll(ITEM_SELECTOR), (elem) =>
          getKey(elem as HTMLElement)
        );
        event.preventDefault();
        setCurrentKey?.(tabBarItemKey[event.key === 'Home' ? 0 : tabBarItemKey.length - 1] as K);
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect?.({ value: currentKey });
      }
      if (event.key === 'Delete') {
        const tabBarItem = findElementByKey(
          rootRef.current,
          currentKey,
          ITEM_SELECTOR
        ) as HTMLElement;
        if (tabBarItem.hasAttribute(REMOVABLE_ITEM_ATTRIBUTE)) {
          onRemove?.({ value: currentKey });
        }
      }
    }
  };

  const onFocus = () => {
    if (rootRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(rootRef.current, ITEM_SELECTOR);
      if (key) {
        setCurrentKey(key as K);
      }
    }
  };

  const eventProps = { onKeyDown, onFocus };

  return {
    rootProps: mergeProps(
      {
        'aria-activedescendant': currentKey ? tabItemPrefix + currentKey : '',
        'aria-multiselectable': false,
        class: className,
        ref: rootRef,
        role: 'tablist',
        tabIndex: 0
      },
      currentKeyProps,
      focusRingProps,
      eventProps
    ),
    tabBarContext: {
      currentKey,
      display,
      isEdgeBottom: edge === 'bottom',
      layout: 'condense',
      onRemove,
      onSelect,
      showFocusRing,
      selection,
      size,
      tabItemPrefix
    }
  } as const;
}

//useCurrentKey use click capture that captures first click, which is on remove button
//when we remove and sets the item being removed to currentKey. So we should not allow
//this if click is on remove button
const extractOnlyItemKey = (element: HTMLElement, itemSelector: string, itemEliminator: string) => {
  const tabBarItem = element.closest(itemSelector);
  if (tabBarItem?.hasAttribute(REMOVABLE_ITEM_ATTRIBUTE)) {
    const removeButton = element.closest(itemEliminator);
    if (removeButton && tabBarItem?.contains(removeButton)) {
      return null;
    }
  }
  return getKey(tabBarItem as HTMLElement);
};

const findNextValidKey = <K>(currentKey: K, currTabs: K[], prevTabs: K[]) => {
  const index = prevTabs.indexOf(currentKey);
  // update current key to be the first one if currentKey is invalid
  if (index === -1) {
    return currTabs[0];
  }
  let nextIndex = 0;
  let isLastKey = false;
  if (index === prevTabs.length - 1) {
    nextIndex = index - 1;
    isLastKey = true;
  } else {
    nextIndex = index + 1;
  }
  while (nextIndex !== index) {
    const nextKey = prevTabs[nextIndex];
    if (currTabs.indexOf(nextKey) !== -1) {
      return nextKey;
    }
    isLastKey ? nextIndex-- : nextIndex++;
  }
  // update current key to be the first one if we can't find a suitable next key
  return currTabs[0];
};
